import { useLayoutEffect, useRef } from 'react';

import { TICKER_ITEMS } from './news-items';

export const TICKER_SPEED_PX_PER_SECOND = 60;

interface NewsTickerProps {
  items?: readonly string[];
}

export default function NewsTicker({ items = TICKER_ITEMS }: NewsTickerProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const track = trackRef.current;
    const content = contentRef.current;
    if (!track || !content) return;

    let previousWidth = 0;
    const updateAnimation = () => {
      const contentWidth = content.getBoundingClientRect().width;
      if (contentWidth <= 0 || Math.abs(contentWidth - previousWidth) < 0.5) return;

      previousWidth = contentWidth;
      track.style.setProperty('--ticker-duration', `${contentWidth / TICKER_SPEED_PX_PER_SECOND}s`);
    };

    updateAnimation();

    if (typeof ResizeObserver === 'undefined') return;

    const resizeObserver = new ResizeObserver(updateAnimation);
    resizeObserver.observe(content);
    return () => resizeObserver.disconnect();
  }, [items]);

  const renderItems = () =>
    items.map((text, index) => (
      <span key={index} className="flex shrink-0 items-center text-xs text-surface-600 sm:text-sm">
        <span>{text}</span>
        <span className="mx-5 text-surface-400" aria-hidden="true">
          ·
        </span>
      </span>
    ));

  return (
    <aside
      className="shrink-0 border-b border-surface-200 bg-surface-100"
      aria-label="Оперативная сводка"
    >
      <div className="mx-auto flex h-9 max-w-[1440px] items-center gap-3 px-4 sm:gap-4 sm:px-6 md:px-8">
        <span className="shrink-0 flex items-center gap-2">
          <span className="h-2 w-2 rounded-sm bg-accent-500" />
          <span className="hidden text-[11px] font-semibold uppercase tracking-[0.12em] text-surface-500 sm:inline">
            Сводка
          </span>
        </span>

        <span className="hidden h-4 w-px shrink-0 bg-surface-300 sm:block" />

        <div className="relative flex-1 overflow-hidden">
          <div
            ref={trackRef}
            className="flex w-max whitespace-nowrap will-change-transform animate-ticker"
          >
            <div ref={contentRef} className="flex shrink-0">
              {renderItems()}
            </div>
            <div className="flex shrink-0" aria-hidden="true">
              {renderItems()}
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}
